import React from "react";
import { format } from "date-fns";
import { useQuery } from "react-query";
import styled from "styled-components";
import { Button } from "@kitbird/core";
import Modal from "../../../../../components/Modal";
import { useWithdrawalReceipt } from "../../hooks/Provider";
import { CustomDatePicker, DatePickerWrapper } from "../../../../../components/CustomDatePicker.style";
import FilterDropdown from "../../../../../components/FilterDropdown";
import CodeTypeOpts from "../../../../../enums/common";
import { IOptionItem } from "../../../../../typings/common";
import useAuth from "../../../../../components/Authentication/useAuth";
import { getBusinessSegment } from "../../../../../services/businessService/businessService";
import { getMasterCompanyRelationByCompanyId } from "../../../../../services/masterCompanyService";
import { ReactComponent as BuildingIcon } from "../../../../../assets/icons/building_icon.svg";
import { ReactComponent as CalendarIcon } from "../../../../../assets/icons/calendar_icon.svg";
import { ReactComponent as LocationIcon } from "../../../../../assets/icons/location_icon.svg";
import { ReactComponent as TaxiIcon } from "../../../../../assets/icons/taxi_icon.svg";

const CustomGrid = styled.div`
  display: grid;
  column-gap: 16px;
  row-gap: 24px;
  grid-template-columns: calc((100% - 16px) / 2) calc((100% - 16px) / 2);
`;

const ModalFilters = () => {
  const { userAccount } = useAuth();
  const {
    modalConfig,
    setModalConfig,
    historyParams,
    setHistoryParams,
    tempHistoryParams,
    setTempHistoryParams,
  } = useWithdrawalReceipt();

  const handleClose = () => {
    setModalConfig(undefined);
    setTempHistoryParams(historyParams);
  };

  const { data: dataCompanies } = useQuery({
    queryKey: ["get_companies", tempHistoryParams.pool, tempHistoryParams.areaId],
    enabled: !!tempHistoryParams.pool && !!tempHistoryParams.areaId,
    queryFn: () => getBusinessSegment(
      userAccount.position_info[0].code_type,
      tempHistoryParams.areaId as string,
      tempHistoryParams.pool?.value as string,
    ),
  });

  const { data: dataCompanyRelations } = useQuery({
    queryKey: ["get_company_relations", tempHistoryParams.company?.value],
    enabled: !!tempHistoryParams.company?.value,
    queryFn: () => getMasterCompanyRelationByCompanyId(tempHistoryParams.company?.value as string),
  });

  return (
    <div style={{ display: modalConfig?.type === "FILTER" ? "unset" : "none" }}>
      <Modal
        isOpen
        setOpen={handleClose}
        title="Filter"
        width={640}
        footer={<>
          <Button
            variant="text"
            onClick={handleClose}
          >Batalkan</Button>
          <Button
            onClick={() => {
              setHistoryParams(tempHistoryParams);
              setModalConfig(undefined);
            }}
            disabled={!(
              tempHistoryParams.date
              && tempHistoryParams.pool
              && tempHistoryParams.areaId
              && (
                (
                  tempHistoryParams.company?.value
                  && tempHistoryParams.company?.value !== "KBBO"
                ) || (
                  tempHistoryParams.company?.value === "KBBO"
                  && tempHistoryParams.companyRelation?.value
                )
              )
            )}
          >Terapkan</Button>
        </>}
        body={<>
          <CustomGrid>
            <DatePickerWrapper style={{ justifyContent: "left" }}>
              <CustomDatePicker
                label="Periode"
                showMonthYearPicker
                variant="default"
                onChange={(date) => {
                  if (date && !Array.isArray(date)) setTempHistoryParams((curr) => ({ ...curr, date: format(date || "", "MMMM-yyyy") || undefined }));
                }}
                onReset={() => setTempHistoryParams((curr) => ({ ...curr, date: undefined }))}
                onSubmit={(date: Date[] | null) => {
                  setTempHistoryParams((curr) => ({ ...curr, date: format(date?.[0] || "", "MMMM-yyyy") || undefined }));
                }}
                value={tempHistoryParams.date}
                dateFormat="LLLL yyyy"
              />
            </DatePickerWrapper>
            <FilterDropdown
              id="code_type"
              label="Tipe user"
              width="284px"
              leftIcon={<TaxiIcon width={16} />}
              value={tempHistoryParams.codeType?.label || ""}
              options={CodeTypeOpts.filter(
                (item) => item.value === userAccount.position_info[0].code_type,
              ) || []}
              onSelected={(val: IOptionItem) => setTempHistoryParams(
                (curr) => ({
                  ...curr,
                  codeType: val as { label: string, value: string },
                  areaId: undefined,
                  pool: undefined,
                  company: undefined,
                }),
              )}
            />

            <FilterDropdown
              id="pool"
              label="Pool"
              width="284px"
              leftIcon={<LocationIcon width={16} />}
              value={tempHistoryParams.pool?.label || ""}
              onSelected={(val: IOptionItem) => setTempHistoryParams(
                (curr) => ({
                  ...curr,
                  areaId: userAccount.position_info.find(
                    (item) => item.pool_id === val.value,
                  )?.area_id,
                  pool: val as { label: string, value: string },
                  company: undefined,
                }),
              )}
              options={userAccount.userPools.map((item) => (
                { label: item.poolName, value: item.poolId }
              ))}
            />
            <FilterDropdown
              id="company"
              label="Perusahaan"
              width="284px"
              leftIcon={<BuildingIcon width={16} />}
              allowSearch
              value={tempHistoryParams.company?.label || ""}
              onSelected={(val: IOptionItem) => setTempHistoryParams(
                (curr) => ({
                  ...curr,
                  company: val as { label: string, value: string },
                  companyRelation: undefined,
                }),
              )}
              options={[...Array.from(new Set(dataCompanies?.lstmstbusiness.map((item) => (
                JSON.stringify({ label: item.company_code, value: item.company_code })
              )))).map((item) => JSON.parse(item)) || []]}
              disable={(!dataCompanies || dataCompanies.lstmstbusiness.length === 0) || undefined}
            />

            <FilterDropdown
              id="company-relation"
              label="Perusahaan relasi"
              width="284px"
              leftIcon={<CalendarIcon width={16} />}
              value={tempHistoryParams.companyRelation?.label || ""}
              onSelected={(val: IOptionItem) => setTempHistoryParams(
                (curr) => ({
                  ...curr,
                  companyRelation: val as { label: string, value: string },
                }),
              )}
              options={dataCompanyRelations?.list_mtcompany.map((item) => (
                { label: item.company_friend, value: item.company_friend }
              )) || []}
              disable={!dataCompanyRelations || dataCompanyRelations.list_mtcompany.length <= 1}
            />
            <div />
          </CustomGrid>
        </>}
      />
    </div>
  );
};

export default ModalFilters;
